import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuthStore } from "../store/authStore";
import { renderInlineMarkdown } from "../lib/markdown";
import { useT } from "../i18n";

interface VersionEntry {
  version: string;
  date: string;
  highlights: string[];
}

interface VersionResponse {
  current_version: string;
  changelog: VersionEntry[];
}

export default function ChangelogPage() {
  const t = useT();
  const G = useAuthStore(s => s.user?.primary_color) || "#0891b2";
  const [changelog, setChangelog] = useState<VersionEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<VersionResponse>("/version")
      .then(d => setChangelog(d.changelog))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="page" style={{ color: "var(--clr-text4)" }}>{t("changelog.loading")}</div>;
  }

  return (
    <div className="page" style={{ color: "var(--clr-text)", maxWidth: 720 }}>
      <h1 style={{ margin: "0 0 0.25rem", fontSize: "1.4rem", fontWeight: 700, letterSpacing: "-0.02em" }}>
        {t("changelog.title")}
      </h1>
      <p style={{ margin: "0 0 2rem", fontSize: "0.82rem", color: "var(--clr-text3)" }}>
        {t("changelog.subtitle")}
      </p>

      <div style={{ position: "relative", paddingLeft: 28 }}>
        {/* Vertical timeline line */}
        <div style={{
          position: "absolute", left: 7, top: 8, bottom: 8,
          width: 1, background: "var(--clr-border2)",
        }} />

        {changelog.map((entry, i) => (
          <div key={entry.version} style={{ position: "relative", marginBottom: i < changelog.length - 1 ? 36 : 0 }}>
            {/* Dot */}
            <div style={{
              position: "absolute", left: -26, top: 8,
              width: 12, height: 12, borderRadius: "50%",
              background: i === 0 ? G : "var(--clr-surface)",
              border: `1px solid ${i === 0 ? G : "var(--clr-border2)"}`,
            }} />

            {/* Card */}
            <div style={{
              background: "var(--clr-surface)",
              border: "1px solid var(--clr-border)",
              borderRadius: 18,
              padding: "1.25rem 1.5rem",
              backdropFilter: "var(--clr-blur)",
              WebkitBackdropFilter: "var(--clr-blur)",
              boxShadow: "var(--clr-shadow-sm)",
            }}>
              {/* Header */}
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: "0.75rem", flexWrap: "wrap" }}>
                <span style={{
                  background: i === 0 ? G : "var(--clr-surface)",
                  color: i === 0 ? "#fff" : "var(--clr-text3)",
                  border: i === 0 ? "1px solid transparent" : "1px solid var(--clr-border)",
                  padding: "3px 10px", borderRadius: 999,
                  fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.5px",
                  fontVariantNumeric: "tabular-nums",
                }}>
                  {entry.version}
                </span>
                {i === 0 && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                    <span style={{ width: 6, height: 6, borderRadius: 3, background: G, display: "inline-block", flexShrink: 0 }} />
                    <span style={{
                      color: "var(--clr-text3)",
                      fontSize: "0.68rem", fontWeight: 500, textTransform: "uppercase", letterSpacing: "1px",
                    }}>
                      {t("changelog.current")}
                    </span>
                  </span>
                )}
                <span style={{ fontSize: "0.78rem", color: "var(--clr-text4)", marginLeft: "auto" }}>
                  {entry.date}
                </span>
              </div>

              {/* Highlights */}
              {entry.highlights.length > 0 ? (
                <ul style={{ margin: 0, paddingLeft: 0, listStyle: "none" }}>
                  {entry.highlights.map((h, j) => (
                    <li key={j} style={{
                      display: "flex", gap: 10, alignItems: "flex-start",
                      marginBottom: j < entry.highlights.length - 1 ? 8 : 0,
                      fontSize: "0.85rem", color: "var(--clr-text)",
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 3, color: "var(--clr-text3)" }}>
                        <path d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                      <span>{renderInlineMarkdown(h)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--clr-text4)" }}>
                  {t("changelog.initialVersion")}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
